import { Map, OrderedMap } from 'immutable';
import BaseObject from './BaseObject';
import { ID } from '../types';
import { Sharee } from './Sharee';

export function shareesFromProps<ShareeType>(
  ShareeClass: { new (shareeData: any): ShareeType },
  props: any,
): OrderedMap<ID, ShareeType> {
  return props.shared_with
    ? OrderedMap((props.shared_with || []).map((sharee) => [sharee.email, new ShareeClass(sharee)]))
    : OrderedMap(props.sharees || []);
}

export function addShareePermission<PermissionType>(
  ShareeClass: { new (shareeData: any): Sharee<PermissionType> },
  shareeEmail: string,
  permission: PermissionType,
  permissionID?: ID,
) {
  return this.copyWith({
    sharees: this.sharees.update(
      shareeEmail,
      new ShareeClass({ email: shareeEmail, permissions: [permission] }),
      (sharee) => sharee.addPermission(permission, permissionID),
    ),
  });
}

export class ShareStatus<PermissionType> extends BaseObject {
  uuid: ID;
  publiclyShared: boolean;
  publicPermissionsMap: Map<PermissionType, true>;
  // set in subclass
  sharees: OrderedMap<ID, Sharee<PermissionType>>;

  constructor(props: any = {}) {
    super(props);
    this.uuid = props.uuid;

    let _publiclyShared = false;
    if (props.hasOwnProperty('public_share')) {
      _publiclyShared = props.public_share;
    } else if (props.hasOwnProperty('publiclyShared')) {
      _publiclyShared = props.publiclyShared;
    }
    this.publiclyShared = _publiclyShared;

    if (props.public_permissions) {
      this.publicPermissionsMap = Map(
        props.public_permissions.map((permission: PermissionType) => [permission, true]),
      );
    } else {
      this.publicPermissionsMap = props.publicPermissionsMap || Map();
    }
  }

  setSharee(sharee: Sharee<PermissionType>) {
    return this.copyWith({ sharees: this.sharees.set(sharee.email, sharee) });
  }

  // implemented in subclass
  addShareePermission(_shareeEmail: string, _permission: PermissionType, _permissionID?: ID) {
    console.error('Method `addShareePermission` must be overridden in subclass');
    return this;
  }

  removeShareePermissionID(sharee: Sharee<PermissionType>, permissionID: ID) {
    return this.copyWith({
      sharees: this.sharees.update(sharee.email, (sharee) =>
        sharee.removePermissionByID(permissionID),
      ),
    });
  }

  removeSharee(sharee: Sharee<PermissionType>) {
    return this.copyWith({ sharees: this.sharees.delete(sharee.email) });
  }

  sharePublicly() {
    return this.copyWith({ publiclyShared: true });
  }

  unsharePublicly() {
    return this.copyWith({ publiclyShared: false });
  }

  addPublicPermission(permission: PermissionType) {
    return this.copyWith({
      publicPermissionsMap: this.publicPermissionsMap.set(permission, true),
    });
  }

  removePublicPermission(permission: PermissionType) {
    return this.copyWith({
      publicPermissionsMap: this.publicPermissionsMap.delete(permission),
    });
  }

  get publicPermissions(): PermissionType[] {
    return this.publicPermissionsMap.keySeq().toArray() || [];
  }

  get isShared(): boolean {
    return this.publiclyShared || this.sharees.size > 0;
  }
}
